/**
 * Working hours: when the company takes bookings, and when each person works.
 *
 * Times are local wall-clock strings ('09:00') in the schedule's own timezone;
 * days are weekday numbers, 0 = Sunday. The default — set here AND as the
 * column defaults in migration 0059 — is 9-to-5 weekdays on the company's own
 * clock.
 *
 * Hours steer rather than fence: the suggester only proposes inside them and
 * the grid shades the rest, but a hand-typed out-of-hours booking is allowed —
 * an emergency Saturday job is the office's call to make.
 *
 * OVERNIGHT WINDOWS. A commercial cleaning crew works 22:00 to 06:00, which is
 * a window whose end time is *smaller* than its start. That shape used to mean
 * one thing here — somebody's hours had been broken by a half-inherited
 * override — and everything downstream treated it as an empty week. It now
 * means two things, told apart by where the two ends came from:
 *
 *   * `endLocal < startLocal` is a window that CROSSES MIDNIGHT. 22:00–06:00
 *     runs from ten at night to six the following morning.
 *   * `endLocal === startLocal` is still nothing at all — a zero-length
 *     window, which no shift has ever been.
 *
 * `days` are the days a shift STARTS, which is how night work is spoken about:
 * a crew that does "Monday to Friday nights" starts on Friday evening and
 * finishes on Saturday morning, and nobody calls that a Saturday shift.
 *
 * The accident this used to catch is caught in `working-hours-guard.ts`
 * instead, by the one question that separates it from a real night shift:
 * whether the layer that made the window cross midnight said BOTH ends, or
 * moved one and inherited the other.
 */

export interface WorkingHours {
  /**
   * Weekday numbers a shift starts on, 0 = Sunday. For a window that crosses
   * midnight these are start days: Friday means Friday night into Saturday.
   */
  days: number[];
  startLocal: string;
  endLocal: string;
  /** Search envelopes for explicit personal profiles bound starts only. */
  unboundedEnd?: boolean;
  extraStartTimes?: string[];
}

/** Minutes past midnight for an 'HH:MM' wall-clock string. */
export function minutesOfTime(timeHHMM: string): number {
  const [hour = '0', minute = '0'] = timeHHMM.split(':');
  return Number(hour) * 60 + Number(minute);
}

/** Minutes past midnight back to 'HH:MM'; anything past a day wraps. */
export function timeOfMinutes(minutes: number): string {
  const wrapped = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

export const MINUTES_PER_DAY = 24 * 60;

/** Does this window run past midnight into the next day? */
export function crossesMidnight(hours: Pick<WorkingHours, 'startLocal' | 'endLocal'>): boolean {
  return hours.endLocal < hours.startLocal;
}

/** A window nobody can work: it ends the instant it starts. */
export function isZeroLengthWindow(hours: Pick<WorkingHours, 'startLocal' | 'endLocal'>): boolean {
  return hours.endLocal === hours.startLocal;
}

/**
 * The window as minutes on a line that starts at the shift day's midnight.
 *
 * An overnight close lands PAST 1440 — 22:00–06:00 is 1320 to 1800 — which is
 * what makes every "is this inside the window", "how long is it" and "where
 * does it sit on the grid" question one subtraction again instead of two
 * branches. Callers that need a wall-clock time back take `close % 1440`.
 */
export function windowMinutes(hours: Pick<WorkingHours, 'startLocal' | 'endLocal'>): {
  open: number;
  close: number;
} {
  const open = minutesOfTime(hours.startLocal);
  const close = minutesOfTime(hours.endLocal);
  return { open, close: crossesMidnight(hours) ? close + MINUTES_PER_DAY : close };
}

/** How long a shift is, in minutes. Zero for a zero-length window. */
export function windowLengthMinutes(hours: Pick<WorkingHours, 'startLocal' | 'endLocal'>): number {
  const { open, close } = windowMinutes(hours);
  return Math.max(0, close - open);
}

export const DEFAULT_WORK_DAYS = [1, 2, 3, 4, 5];
export const DEFAULT_WORK_START = '09:00';
export const DEFAULT_WORK_END = '17:00';

/** '1,2,3' → [1, 2, 3]; anything unparseable falls back to weekdays. */
export function parseWorkDays(raw: string | null | undefined): number[] {
  if (!raw) return [...DEFAULT_WORK_DAYS];
  const days = [...new Set(raw.split(',').map((part) => Number(part.trim())))]
    .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
    .sort((a, b) => a - b);
  return days.length > 0 ? days : [...DEFAULT_WORK_DAYS];
}

/**
 * The three columns as any row carrying working hours spells them — the
 * company's (never null), a location's, or a person's.
 */
export interface WorkingHoursColumns {
  work_day_start_local?: string | null;
  work_day_end_local?: string | null;
  work_days?: string | null;
}

/** The company's hours off its row; missing columns read as the default. */
export function companyWorkingHours(company: WorkingHoursColumns): WorkingHours {
  return {
    days: parseWorkDays(company.work_days),
    startLocal: company.work_day_start_local || DEFAULT_WORK_START,
    endLocal: company.work_day_end_local || DEFAULT_WORK_END,
  };
}

/**
 * One layer of overrides on top of hours already resolved.
 *
 * Column by column, not as a set: a branch that only opens earlier says so
 * with a start time and inherits the days and the closing time it never had
 * an opinion about. Null is what inheriting looks like in the database, and
 * it means the same thing at every level.
 */
export function overrideWorkingHours(
  base: WorkingHours,
  override: WorkingHoursColumns | null | undefined,
): WorkingHours {
  if (!override) return base;
  return {
    days: override.work_days ? parseWorkDays(override.work_days) : base.days,
    startLocal: override.work_day_start_local || base.startLocal,
    endLocal: override.work_day_end_local || base.endLocal,
  };
}

/**
 * The window a branch keeps: its own where set, the company's where not.
 *
 * This is the base every other hours question is asked against — a person's
 * override sits on top of THEIR BRANCH's hours, not the company's, because a
 * cleaner who starts an hour before their branch opens means an hour before
 * that branch, not before head office.
 */
export function locationWorkingHours(
  company: WorkingHoursColumns,
  location: WorkingHoursColumns | null | undefined,
): WorkingHours {
  return overrideWorkingHours(companyWorkingHours(company), location);
}

/** A person's hours, over whatever their branch (or the company) works. */
export function personWorkingHours(base: WorkingHours, person: WorkingHoursColumns): WorkingHours {
  return overrideWorkingHours(base, person);
}

/**
 * The window a whole crew shares: the days everyone works, from the latest
 * start to the earliest end. A crew with no common ground yields empty days —
 * an honest "these people never overlap", not an error.
 *
 * Done on the minute line rather than by comparing 'HH:MM' strings, because
 * once a window may cross midnight the strings stop sorting the way the clock
 * runs: 06:00 reads as earlier than 22:00 while the shift it ends is later.
 * On the line a night window is simply 1320→1800 and the same two
 * comparisons — latest open, earliest close — are right again.
 *
 * That makes the useful answers fall out on their own. A day person and a
 * night person intersect to nothing, which is true and used to be true by
 * accident. Two night people intersect to the hours they share, which used to
 * be impossible to express.
 */
export function intersectWorkingHours(all: readonly WorkingHours[]): WorkingHours {
  if (all.length === 0)
    return { days: [...DEFAULT_WORK_DAYS], startLocal: DEFAULT_WORK_START, endLocal: DEFAULT_WORK_END };
  let days = all[0]!.days;
  let { open, close } = windowMinutes(all[0]!);
  for (const hours of all.slice(1)) {
    const daySet = new Set(hours.days);
    days = days.filter((day) => daySet.has(day));
    const window = windowMinutes(hours);
    if (window.open > open) open = window.open;
    if (window.close < close) close = window.close;
  }
  // No shared ground: the crew's windows never overlap. Reported as no working
  // days rather than as an error, and the times are left as the collapsed pair
  // so a reader can see WHERE it collapsed.
  if (close <= open) days = [];
  return { days, startLocal: timeOfMinutes(open), endLocal: timeOfMinutes(close) };
}

/**
 * Would applying `override` to `base` make the window cross midnight BY
 * ACCIDENT — the failure the layering has always been able to cause?
 *
 * Working hours resolve through three layers and each overrides column by
 * column, so half of anybody's window can be inherited. That is the feature: a
 * branch that only opens earlier says so with a start time and keeps head
 * office's closing time. It is also how a company moving its day to 08:00–16:00
 * can leave a branch that opens at 18:00 reading 18:00–16:00 — a window nobody
 * typed and nobody wants.
 *
 * A real night shift and that accident are the same three columns. What tells
 * them apart is who said what: a layer that names BOTH ends and puts them the
 * wrong way round has declared a night shift, while a layer that moves one end
 * and inherits the other has stumbled into one. Inheriting a window that
 * already crossed midnight is not an accident either — that is a night branch's
 * crew working nights, which is the whole point.
 */
export function acquiresOvernightByAccident(
  base: WorkingHours,
  override: WorkingHoursColumns | null | undefined,
): boolean {
  const result = overrideWorkingHours(base, override);
  if (!crossesMidnight(result)) return false;
  if (crossesMidnight(base)) return false;
  return !(override?.work_day_start_local && override?.work_day_end_local);
}
